import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useConnectBankMutation, useCurrentUserAccountsDetailsQuery, CurrentUserAccountsDetailsDocument } from "../generated/graphql";
import ErrorMessage from "./ErrorMessage";
import Toast from "./Toast";
import { useToast } from "../hooks/useToast";

const AccountsConnect: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [connectBank, { loading: connecting, error: connectError }] = useConnectBankMutation();
  const { refetch: refetchAccounts } = useCurrentUserAccountsDetailsQuery({ skip: true });
  const { toast, setToast } = useToast();
  const [connectionProcessed, setConnectionProcessed] = useState(false);
  const [tokenReady, setTokenReady] = useState(false);
  
  const error = searchParams.get("error");
  const errorDescription = searchParams.get("error_description");
  const code = searchParams.get("code");
  const connectionId = searchParams.get("connection_id");
  const state = searchParams.get("state");

  const getErrorMessage = (errorCode: string): string => {
    const errorMessages: Record<string, string> = {
      access_denied: "L'accès a été refusé. Veuillez réessayer.",
      invalid_request: "La requête est invalide. Veuillez réessayer.",
      server_error: "Une erreur serveur est survenue. Veuillez réessayer plus tard.",
      temporarily_unavailable: "Le service est temporairement indisponible. Veuillez réessayer plus tard.",
    };
    return errorMessages[errorCode] || "Une erreur est survenue lors de la connexion de la banque.";
  };

  // Vérifier que le token est disponible avant d'appeler connectBank
  useEffect(() => {
    if (isAuthenticated) {
      getAccessTokenSilently({
        authorizationParams: {
          scope: "read:current_user openid profile email offline_access",
          audience: "https://julientassin.eu.auth0.com/api/v2/",
        },
      })
        .then(() => {
          // Attendre un court délai pour s'assurer que le client Apollo a bien le token
          setTimeout(() => {
            setTokenReady(true);
          }, 1000);
        })
        .catch((err: any) => {
          console.error("Erreur lors de la récupération du token:", err);
          setToast({ message: "Erreur d'authentification. Veuillez vous reconnecter.", type: "error" });
        });
    }
  }, [isAuthenticated, getAccessTokenSilently, setToast]);

  useEffect(() => {
    // Log des paramètres pour le debug (error_description n'est pas affiché à l'utilisateur)
    console.log("Callback parameters:", {
      error,
      errorDescription, // Pour le debug uniquement
      code,
      connectionId,
      allParams: Object.fromEntries(searchParams.entries()),
    });
  }, [error, errorDescription, code, connectionId, searchParams]);

  // Flow connect classique (webview sans code pré-passé) : code + connection_id en callback
  useEffect(() => {
    if (!error && connectionId && code && !connectionProcessed && tokenReady && isAuthenticated) {
      setConnectionProcessed(true);
      connectBank({
        variables: {
          budgetInsideClientID: connectionId,
          budgetInsideClientCode: decodeURIComponent(code),
        },
        refetchQueries: [{ query: CurrentUserAccountsDetailsDocument }],
      })
        .then(() => {
          setToast({ message: "Banque connectée avec succès", type: "success" });
          setTimeout(() => {
            navigate("/accounts");
          }, 2000);
        })
        .catch((err: any) => {
          console.error("Erreur lors de la connexion de la banque:", err);
          const errorMessage = err?.message || "Une erreur est survenue lors de la connexion de la banque";
          setToast({ message: errorMessage, type: "error" });
        });
    }
  }, [error, code, connectionId, connectionProcessed, connectBank, navigate, tokenReady, isAuthenticated, setToast]);

  // Flow nouvelle banque (webview avec code pré-passé) : seul connection_id revient, pas de code
  useEffect(() => {
    if (!error && connectionId && !code && !connectionProcessed && tokenReady && isAuthenticated) {
      setConnectionProcessed(true);
      refetchAccounts()
        .then(() => {
          setToast({ message: "Banque connectée avec succès", type: "success" });
          setTimeout(() => {
            navigate("/accounts");
          }, 2000);
        })
        .catch((err: any) => {
          console.error("Erreur lors du rafraîchissement des données:", err);
          setToast({ message: "Banque connectée, mais erreur lors du rafraîchissement", type: "error" });
        });
    }
  }, [error, code, connectionId, connectionProcessed, refetchAccounts, navigate, tokenReady, isAuthenticated, setToast]);

  const isManageCallback = !error && !code && !connectionId && state === null;
  const isReconnectCallback = !error && !code && !connectionId && state !== null;
  const isNewBankCallback = !error && connectionId && !code;

  // Flow manage (retour Powens sans aucun param) : juste rafraîchir et rediriger
  useEffect(() => {
    if (isManageCallback && !connectionProcessed && isAuthenticated) {
      setConnectionProcessed(true);
      refetchAccounts()
        .then(() => {
          navigate("/accounts");
        })
        .catch(() => {
          navigate("/accounts");
        });
    }
  }, [isManageCallback, connectionProcessed, refetchAccounts, navigate, isAuthenticated]);

  // Gérer le callback du flow reconnect (pas de code ni connection_id, juste state)
  useEffect(() => {
    if (isReconnectCallback && !connectionProcessed) {
      setConnectionProcessed(true);
      refetchAccounts().then(() => {
        setToast({ message: "Banque ré-authentifiée avec succès", type: "success" });
        setTimeout(() => {
          navigate("/accounts");
        }, 2000);
      }).catch((err: any) => {
        console.error("Erreur lors du rafraîchissement des données:", err);
        setToast({ message: "Banque ré-authentifiée, mais erreur lors du rafraîchissement", type: "error" });
      });
    }
  }, [isReconnectCallback, connectionProcessed, refetchAccounts, navigate, setToast]);

  if (error) {
    const userFriendlyMessage = getErrorMessage(error);
    return (
      <div className="container mx-auto">
        <h1 className="text-2xl font-bold mb-6">Connexion de compte bancaire</h1>
        <ErrorMessage
          error={new Error(userFriendlyMessage)}
          title="Erreur lors de la connexion de la banque"
          className="mb-6"
        />
        <div className="mt-4">
          <button
            onClick={() => navigate("/accounts")}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-blue-500"
          >
            Retour aux comptes
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <div className="container mx-auto">
        <h1 className="text-2xl font-bold mb-6">Connexion de compte bancaire</h1>
        {connectError ? (
          <ErrorMessage
            error={connectError}
            title="Erreur lors de la connexion de la banque"
            className="mb-6"
          />
        ) : (code && connectionId) ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-green-800 mb-2">
              {connecting ? "Connexion en cours..." : "Connexion réussie"}
            </h2>
            {connecting ? (
              <p className="text-sm text-green-700 mb-4">
                Traitement de la connexion bancaire en cours...
              </p>
            ) : (
              <>
                <p className="text-sm text-green-700 mb-2">
                  La banque a été connectée avec succès.
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  Redirection vers la page des comptes...
                </p>
              </>
            )}
          </div>
        ) : isNewBankCallback ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-green-800 mb-2">
              Banque ajoutée avec succès
            </h2>
            <p className="text-sm text-green-700 mb-2">
              La nouvelle banque a été connectée à votre compte.
            </p>
            <p className="text-sm text-gray-600 mb-4">
              Redirection vers la page des comptes...
            </p>
          </div>
        ) : isReconnectCallback ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-green-800 mb-2">
              Ré-authentification réussie
            </h2>
            <p className="text-sm text-green-700 mb-2">
              La banque a été ré-authentifiée avec succès.
            </p>
            <p className="text-sm text-gray-600 mb-4">
              Redirection vers la page des comptes...
            </p>
          </div>
        ) : isManageCallback ? (
          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
            <p className="text-gray-500 italic text-center">
              Redirection en cours...
            </p>
          </div>
        ) : (
          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
            <p className="text-gray-500 italic text-center">
              En attente de la redirection...
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default AccountsConnect;

